import { SidebarOverlay, Switch, useTheme } from "@bfc-software/pallet";
import { AuthContext, getEnv } from "@bfc-software/app-toolbox";
import { format } from "date-fns";
import bfcLogo from "/bfc-main.svg";
import { Link, useParams } from "@tanstack/react-router";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { useContext } from "react";

import styles from "./primary-sidebar.module.css";

const linkParams = (close: () => void) => ({
  onClick: close,
  activeProps: {
    className: styles.active,
  },
  className: styles.link,
});

function TopLevelLinks({ close }: { close: () => void }) {
  const { lang, location, org } = useParams({ strict: false });

  const { t } = useTranslation({
    lang: lang ?? "",
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  if (
    typeof lang !== "string" ||
    typeof location !== "string" ||
    typeof org !== "string"
  ) {
    return <></>;
  }

  return (
    <div className={styles.linkGroup}>
      <h3 className={styles.linkGroupHeading}>{t("sidebar.topGroup.title")}</h3>
      <ul className={styles.linkList}>
        <li>
          <Link
            {...linkParams(close)}
            to="/$lang/orgs/$org/internal/whs/$location/home"
            params={{ lang, location, org }}
          >
            {t("sidebar.topGroup.homeLink")}
          </Link>
        </li>
      </ul>
    </div>
  );
}

function ImportLinks({ close }: { close: () => void }) {
  const { lang, location, org } = useParams({ strict: false });

  const { t } = useTranslation({
    lang: lang ?? "",
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  if (
    typeof lang !== "string" ||
    typeof location !== "string" ||
    typeof org !== "string"
  ) {
    return <></>;
  }

  return (
    <div className={styles.linkGroup}>
      <h3 className={styles.linkGroupHeading}>
        {t("sidebar.importGroup.title")}
      </h3>
      <ul className={styles.linkList}>
        <li>
          <Link
            {...linkParams(close)}
            search={{
              page: 0,
              q: "",
              view: "all",
              range: "24h",
              fileTypes: "",
            }}
            to="/$lang/orgs/$org/internal/whs/$location/ftp"
            params={{ lang, location, org }}
          >
            {t("sidebar.importGroup.ftpLink")}
          </Link>
        </li>
      </ul>
    </div>
  );
}

function OrchestrationLinks({ close }: { close: () => void }) {
  const { lang, location, org } = useParams({ strict: false });

  const { t } = useTranslation({
    lang: lang ?? "",
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  if (
    typeof lang !== "string" ||
    typeof location !== "string" ||
    typeof org !== "string"
  ) {
    return <></>;
  }

  return (
    <div className={styles.linkGroup}>
      <h3 className={styles.linkGroupHeading}>
        {t("sidebar.orchestrationGroup.title")}
      </h3>
      <ul className={styles.linkList}>
        <li>
          <Link
            {...linkParams(close)}
            to="/$lang/orgs/$org/internal/whs/$location/task-plans"
            params={{ lang, location, org }}
          >
            {t("sidebar.orchestrationGroup.taskPlansLink")}
          </Link>
        </li>
        <li>
          <Link
            {...linkParams(close)}
            to="/$lang/orgs/$org/internal/whs/$location/selector-sessions"
            params={{ lang, location, org }}
          >
            {t("sidebar.orchestrationGroup.selectorSessionsLink")}
          </Link>
        </li>
        <li>
          <Link
            {...linkParams(close)}
            to="/$lang/orgs/$org/internal/whs/$location/webhooks/endpoints"
            params={{ lang, location, org }}
          >
            {t("sidebar.orchestrationGroup.webhookEndpointsLink")}
          </Link>
        </li>
      </ul>
    </div>
  );
}

export function PrimarySidebar() {
  const { lang } = useParams({ strict: false });
  const authContext = useContext(AuthContext);
  const { theme, setTheme } = useTheme();

  const { t } = useTranslation({
    lang: lang ?? "",
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const { tag, buildDate, version } = getEnv();

  return (
    <SidebarOverlay
      trigger={{
        variant: "tertiary",
        icon: { type: "menu" },
      }}
      header={<img src={bfcLogo} alt="BFC logo" />}
    >
      {({ close }) => (
        <div className={styles.sidebarInnerContent}>
          <TopLevelLinks close={close} />
          <ImportLinks close={close} />
          <OrchestrationLinks close={close} />

          <div className={styles.sidebarFooterContent}>
            <div className={styles.sidebarFooterLogout}>
              <Link
                to="/$lang/auth/login"
                params={{ lang: lang! }}
                onClick={() => authContext.logout()}
              >
                {t("sidebar.logout")}
              </Link>
              <Switch
                label={t("sidebar.darkMode")}
                name="dark-mode"
                value="dark"
                isSelected={theme === "dark"}
                onChange={(selected) => setTheme(selected ? "dark" : "light")}
                display={{ orientation: "row-reverse" }}
              />
            </div>
            <div className={styles.sidebarFooterVersion}>
              <span>
                {t("version")}:{" "}
                {(typeof tag === "string"
                  ? tag
                  : (version ?? "unknown")
                ).substring(0, 20)}
              </span>
              <span className={styles.buildDate}>
                {format(new Date(buildDate), "MMM d, yyyy")}
              </span>
            </div>
          </div>
        </div>
      )}
    </SidebarOverlay>
  );
}
